import React, { lazy, Suspense, useState } from "react";
import { CalendarDays, ChefHat, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import SegmentedControl from "./ui/SegmentedControl";
import ShimmerSkeleton from "./ui/ShimmerSkeleton";
import telegramService from "../utils/telegram";

const MealPlan = lazy(() => import("./MealPlan"));
const Recipes = lazy(() => import("./Recipes"));

const Plan: React.FC = () => {
  const { t } = useTranslation();
  const [section, setSection] = useState<"mealPlan" | "recipes">("mealPlan");

  return (
    <div className="mx-auto min-h-screen w-full max-w-md overflow-x-hidden bg-app pb-28">
      <div className="sticky top-0 z-10 border-b border-line bg-surface-elevated px-4 pb-4 pt-[calc(1.25rem+var(--safe-top))] backdrop-blur-md">
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-content-tertiary">
              {t("plan.eyebrow")}
            </p>
            <h1 className="mt-1 text-xl font-black text-content-primary">{t("plan.title")}</h1>
            <p className="mt-0.5 text-xs font-medium text-content-tertiary">
              {t("plan.subtitle")}
            </p>
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-soft text-brand-text">
            <Sparkles size={22} />
          </div>
        </div>

        <SegmentedControl
          segments={[
            { id: "mealPlan", label: t("mealPlan.title") },
            { id: "recipes", label: t("recipes.title") },
          ]}
          active={section}
          onChange={(id) => {
            telegramService.haptic("selection");
            setSection(id as "mealPlan" | "recipes");
          }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 px-4 py-4">
        <button
          onClick={() => setSection("mealPlan")}
          className="rounded-3xl border border-line bg-surface p-4 text-left shadow-card active:scale-[0.98] transition-transform"
        >
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-premium-soft text-premium">
            <CalendarDays size={20} />
          </div>
          <p className="text-sm font-black text-content-primary">{t("plan.weeklyPlan")}</p>
          <p className="mt-1 text-[11px] font-medium text-content-tertiary">
            {t("plan.weeklyPlanDesc")}
          </p>
        </button>

        <button
          onClick={() => setSection("recipes")}
          className="rounded-3xl border border-line bg-surface p-4 text-left shadow-card active:scale-[0.98] transition-transform"
        >
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-success-soft text-success">
            <ChefHat size={20} />
          </div>
          <p className="text-sm font-black text-content-primary">{t("plan.nextMeal")}</p>
          <p className="mt-1 text-[11px] font-medium text-content-tertiary">
            {t("plan.nextMealDesc")}
          </p>
        </button>
      </div>

      <Suspense fallback={<ShimmerSkeleton variant={section === "mealPlan" ? "mealPlan" : "recipes"} />}>
        {section === "mealPlan" ? <MealPlan embedded /> : <Recipes embedded />}
      </Suspense>
    </div>
  );
};

export default Plan;
